export interface CompanySearchResult {
  domain: string;
  name: string;
}

export function normalizeCompanyDomain(value: string): string {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return "";

  try {
    const withProtocol = trimmed.includes("://") ? trimmed : `https://${trimmed}`;
    const hostname = new URL(withProtocol).hostname.replace(/^www\./, "");
    return hostname || trimmed;
  } catch {
    return trimmed.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0] ?? trimmed;
  }
}

export function companyDomainKey(companyName: string): string {
  return companyName.trim().toLowerCase();
}

const COMPANY_DOMAIN_STORAGE_KEY = "liminl.companyDomains";

export function readStoredCompanyDomain(companyName: string): string | null {
  const key = companyDomainKey(companyName);
  if (!key) return null;

  try {
    const raw = window.localStorage.getItem(COMPANY_DOMAIN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, string>;
    const domain = parsed[key];
    return domain ? normalizeCompanyDomain(domain) : null;
  } catch {
    return null;
  }
}

export function storeCompanyDomain(companyName: string, domain: string) {
  const key = companyDomainKey(companyName);
  const normalized = normalizeCompanyDomain(domain);
  if (!key || !normalized) return;

  try {
    const raw = window.localStorage.getItem(COMPANY_DOMAIN_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    parsed[key] = normalized;
    window.localStorage.setItem(COMPANY_DOMAIN_STORAGE_KEY, JSON.stringify(parsed));
  } catch {
    // best effort
  }
}

const COMPANY_DOMAIN_MAP: Record<string, string> = {
  Google: "google.com",
  Notion: "notion.so",
  Stripe: "stripe.com",
  Linear: "linear.app",
  Ramp: "ramp.com",
  Watershed: "watershed.com",
  Arcadia: "arcadia.com",
  Pulley: "pulley.com",
  Anthropic: "anthropic.com",
  Mercury: "mercury.com",
  Canva: "canva.com",
  Airtable: "airtable.com",
  Figma: "figma.com",
  Asana: "asana.com",
  Meta: "meta.com",
  Adobe: "adobe.com",
  Sketch: "sketch.com",
  Spotify: "spotify.com",
  Airbnb: "airbnb.com",
  Pavago: "pavago.co",
  "Josie Maran": "josiemaran.com",
  HighLevel: "gohighlevel.com",
  "Creative Fabrica": "creativefabrica.com",
  RECSEEKERS: "recseekers.com",
  Sundayy: "sundayy.com",
  Velsera: "velsera.com",
};

function guessCompanyDomain(company: string): string {
  return `${company.trim().toLowerCase().replace(/[^a-z0-9]/g, "")}.com`;
}

export function resolveCompanyDomain(
  company: string,
  explicitDomain?: string | null,
): string {
  const trimmed = company.trim();
  if (!trimmed) return "example.com";

  const override = explicitDomain ? normalizeCompanyDomain(explicitDomain) : "";
  if (override) return override;

  const stored = readStoredCompanyDomain(trimmed);
  if (stored) return stored;

  const mapped = COMPANY_DOMAIN_MAP[trimmed];
  if (mapped) return mapped;

  return guessCompanyDomain(trimmed);
}

export function companyDomainFor(company: string, explicitDomain?: string | null): string {
  return resolveCompanyDomain(company, explicitDomain);
}

export function companyFaviconUrl(
  company: string,
  size = 64,
  explicitDomain?: string | null,
): string {
  const domain = resolveCompanyDomain(company, explicitDomain);
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=${size}`;
}

function rankLocalCompanies(query: string, limit = 6): CompanySearchResult[] {
  const normalized = query.trim().toLowerCase();
  if (normalized.length < 2) return [];

  const startsWith: CompanySearchResult[] = [];
  const includes: CompanySearchResult[] = [];

  for (const [name, domain] of Object.entries(COMPANY_DOMAIN_MAP)) {
    const lowerName = name.toLowerCase();
    const entry = { name, domain };
    if (lowerName.startsWith(normalized) || domain.startsWith(normalized)) {
      startsWith.push(entry);
    } else if (lowerName.includes(normalized) || domain.includes(normalized)) {
      includes.push(entry);
    }
  }

  return [...startsWith, ...includes].slice(0, limit);
}

export function searchLocalCompanies(query: string, limit = 6): CompanySearchResult[] {
  return rankLocalCompanies(query, limit);
}

export async function searchCompanies(
  query: string,
  signal?: AbortSignal,
): Promise<CompanySearchResult[]> {
  const trimmed = query.trim();
  if (trimmed.length < 2) return [];

  try {
    const response = await fetch(
      `https://autocomplete.clearbit.com/v1/companies/suggest?query=${encodeURIComponent(trimmed)}`,
      { signal },
    );
    if (response.ok) {
      const payload = (await response.json()) as Array<{ domain?: string; name?: string }>;
      const remote = payload
        .map((item) => ({
          name: item.name?.trim() ?? "",
          domain: item.domain ? normalizeCompanyDomain(item.domain) : "",
        }))
        .filter((item): item is CompanySearchResult => Boolean(item.name && item.domain));

      if (remote.length > 0) return remote.slice(0, 8);
    }
  } catch {
    // fall through to local suggestions
  }

  const local = searchLocalCompanies(trimmed, 6);
  if (local.length > 0) return local;

  const guessedDomain = guessCompanyDomain(trimmed);
  return [{ name: trimmed, domain: guessedDomain }];
}

/** Damerau-Levenshtein (allows adjacent transposition - rmap ↔ ramp). */
function editDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i += 1) dp[i]![0] = i;
  for (let j = 0; j <= n; j += 1) dp[0]![j] = j;
  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i]![j] = Math.min(
        (dp[i - 1]![j] ?? 0) + 1,
        (dp[i]![j - 1] ?? 0) + 1,
        (dp[i - 1]![j - 1] ?? 0) + cost,
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        dp[i]![j] = Math.min(dp[i]![j] ?? 0, (dp[i - 2]![j - 2] ?? 0) + 1);
      }
    }
  }
  return dp[m]![n] ?? Math.max(m, n);
}

function companyMatchScore(query: string, candidate: string): number {
  const q = query.trim().toLowerCase();
  const c = candidate.trim().toLowerCase();
  if (!q || !c) return 0;
  if (q === c) return 100;
  if (c.startsWith(q) || q.startsWith(c)) return 92;
  const dist = editDistance(q, c);
  const maxLen = Math.max(q.length, c.length);
  if (dist === 1 && maxLen >= 3) return 88;
  if (dist === 2 && maxLen >= 4) return 76;
  return 0;
}

const AUTO_CORRECT_MIN_SCORE = 76;

/** Sync correction against the local company map only (no network). */
export function resolveTypedCompanyNameLocal(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;

  let bestName = "";
  let bestScore = 0;
  for (const name of Object.keys(COMPANY_DOMAIN_MAP)) {
    const score = companyMatchScore(trimmed, name);
    if (score > bestScore) {
      bestScore = score;
      bestName = name;
    }
  }
  if (bestScore >= AUTO_CORRECT_MIN_SCORE && bestName) return bestName;
  return trimmed;
}

/**
 * Resolve a free-typed company token to a canonical name when confidence is
 * high (typos like "rmap" → "Ramp"). Leaves unknown / low-confidence tokens as-is.
 */
export async function resolveTypedCompanyName(
  raw: string,
  signal?: AbortSignal,
): Promise<string> {
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;

  const local = resolveTypedCompanyNameLocal(trimmed);
  if (local !== trimmed && companyMatchScore(trimmed, local) >= 88) return local;

  let bestName = local !== trimmed ? local : "";
  let bestScore = local !== trimmed ? companyMatchScore(trimmed, local) : 0;

  try {
    const remote = await searchCompanies(trimmed, signal);
    for (const hit of remote) {
      // Ignore the "guessed .com" fallback that echoes the typo back.
      if (hit.domain === guessCompanyDomain(trimmed) && companyMatchScore(trimmed, hit.name) < 88) {
        continue;
      }
      const score = companyMatchScore(trimmed, hit.name);
      if (score > bestScore) {
        bestScore = score;
        bestName = hit.name;
      }
    }
  } catch {
    // keep local best
  }

  if (bestScore >= AUTO_CORRECT_MIN_SCORE && bestName) return bestName;
  return trimmed;
}

function splitCompanyTokens(value: string): string[] {
  return value
    .split(/[,;]/)
    .flatMap((part) => part.split(/\s+&\s+|\s+and\s+/i))
    .map((part) => part.trim())
    .filter(Boolean);
}

/** Sync split + local spell-correct (for overview / display). */
export function resolveTypedCompanyListLocal(value: string): string[] {
  return Array.from(
    new Set(splitCompanyTokens(value).map((part) => resolveTypedCompanyNameLocal(part)).filter(Boolean)),
  );
}

/** Split + spell-correct a comma/and-separated company list. */
export async function resolveTypedCompanyList(
  value: string,
  signal?: AbortSignal,
): Promise<string[]> {
  const parts = splitCompanyTokens(value);
  const resolved = await Promise.all(parts.map((part) => resolveTypedCompanyName(part, signal)));
  return Array.from(new Set(resolved.filter(Boolean)));
}
